import { HttpError, User } from "@/models/auth";
import { FindInstruments } from "@/models/find-instruments";
import { RightColumnModule as StockShortsRightColumnModule } from "@/models/stock-shorts";
import { ModuleTemplate, TabTemplate } from "@/models/custom-views";
import { Universe, UserUniverses } from "@/models/universe";
import { isUndefined } from "lodash";
import { atom, selector } from "recoil";
import { v4 as uuidv4 } from "uuid";
import { Instrument } from "@/models/instruments";
import { Notification } from "@/models/notifications";
import { Plan, Subscription } from "@/models/subscription";
import { brighten } from "./ortex-charts/color";

export const themeState = atom<"dark" | "light">({
    key: "themeState",
    default: undefined,
});

// Display contrast (normal | high). High contrast is a dark-mode modifier:
// redesign.scss swaps the CSS tokens, and _app.tsx swaps the colour atoms
// below so charts and inline-painted colours follow too.
// Alt Data upgrade dialog (Visitor / Free / Basic hitting an Alt Data entry point).
export const altDataUpgradeDialogState = atom<boolean>({
    key: "altDataUpgradeDialogState",
    default: false,
});

export const contrastState = atom<"normal" | "high">({
    key: "contrastState",
    default: "normal",
});

// Colours the account has saved (raw values from the settings payload).
// Undefined = never saved. Used so manual colour choices win in every display
// mode, including high contrast.
export type UserColorKey = "positive" | "negative" | "grey" | "blackMedium";
export const userColorSettingsState = atom<Partial<Record<UserColorKey, string>>>({
    key: "userColorSettingsState",
    default: {},
});

// Every default a colour setting has ever had. A saved value equal to one of
// these is not a manual choice (the settings page persists defaults too).
const COLOR_DEFAULT_HISTORY: Record<UserColorKey, string[]> = {
    positive: ["rgb(49,171,166)", "rgb(53,196,138)", "rgb(30,160,105)"],
    negative: ["rgb(239,68,56)", "rgb(239,90,106)", "rgb(226,66,80)"],
    grey: ["rgb(128,128,128)", "rgb(151,161,178)", "rgb(161,166,173)"],
    blackMedium: ["rgb(21,30,35)", "rgb(13,17,26)", "rgb(22,24,27)"],
};
const normalizeColor = (c: string) => c.replace(/\s+/g, "").toLowerCase();
export const isManualColor = (key: UserColorKey, value: string | undefined): boolean => {
    if (!value) return false;
    const v = normalizeColor(value);
    return !COLOR_DEFAULT_HISTORY[key].some((d) => normalizeColor(d) === v);
};

export const HIGH_CONTRAST_COLORS = {
    ortexColor: "rgb(46, 230, 223)",
    lighterOrtexColor: "rgba(46, 230, 223, 0.7)",
    positive: "rgb(0, 255, 136)",
    negative: "rgb(255, 77, 77)",
    grey: "rgb(208, 208, 208)",
    blackMedium: "rgb(0, 0, 0)",
    darkDefault: "rgb(0, 0, 0)",
    darkSurfaceDefault: "rgb(16, 16, 16)",
    iconColor: "rgb(208, 208, 208)",
    dividerDark: "rgb(58, 58, 58)",
    borderDark: "rgb(90, 90, 90)",
    popoverContent: "rgb(16, 16, 16)",
};

export const universeState = atom<Universe>({
    key: "universeState",
    default: undefined,
});

export const universesState = atom<UserUniverses>({
    key: "universesState",
    default: undefined,
});

export const regionState = atom<7 | 10>({
    key: "regionState",
    default: undefined,
});

export const ortexColorState = atom<string>({
    key: "ortexColor",
    default: "rgb(49, 171, 166)",
});

export const lighterOrtexColorState = atom<string>({
    key: "lighterOrtexColor",
    default: "rgba(49, 171, 166, 0.7)",
});

export const newsOptionsState = atom({
    key: "newsOptionsState",
    default: {},
});

export const positiveState = atom<string>({
    key: "positiveState",
    default: "rgb(30,160,105)",
});

export const positiveGradientState = selector<string>({
    key: "positiveGradientState",
    get: ({ get }) => {
        const positiveColor = get(positiveState);

        return "linear-gradient(270deg," + positiveColor + " 0%, transparent 100%)";
    },
});

export const negativeState = atom<string>({
    key: "negativeState",
    default: "rgb(226,66,80)",
});

export const negativeGradientState = selector<string>({
    key: "negativeGradientState",
    get: ({ get }) => {
        const negativeColor = get(negativeState);

        return "linear-gradient(90deg," + negativeColor + " 0%, transparent 100%)";
    },
});

export const neutralState = atom<string>({
    key: "neutralState",
    default: "rgb(171,133,109)",
});

export const greyState = atom<string>({
    key: "greyState",
    default: "rgb(161,166,173)",
});

export const blackState = atom<string>({
    key: "blackState",
    default: "rgb(0,0,0)",
});

export const whiteState = atom<string>({
    key: "whiteState",
    default: "rgb(255,255,255)",
});

export const blackMediumState = atom<string>({
    key: "blackMediumState",
    default: "rgb(22, 24, 27)",
});

export const darkDefaultState = atom<string>({
    key: "darkDefaultState",
    default: "rgb(15, 16, 18)",
});

export const darkSurfaceDefaultState = atom<string>({
    key: "darkSurfaceDefaultState",
    default: "rgb(22, 24, 27)",
});

export const whiteMediumState = atom<string>({
    key: "whiteMediumState",
    default: "rgb(244, 245, 247)",
});

export const iconColorState = atom<string>({
    key: "iconColorState",
    default: "rgb(161, 166, 173)",
});

export const dividerDarkState = atom<string>({
    key: "dividerDarkState",
    default: "rgb(38, 41, 46)",
});

export const dividerLightState = atom<string>({
    key: "dividerLightState",
    default: "rgb(227,229,233)",
});

export const borderDarkState = atom<string>({
    key: "borderDarkState",
    default: "rgb(54, 58, 65)",
});

export const borderLightState = atom<string>({
    key: "borderLightState",
    default: "rgb(207, 211, 217)",
});

export const dividerState = selector<string>({
    key: "dividerState",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(dividerDarkState) : get(dividerLightState);
    },
});

export const borderColorState = selector<string>({
    key: "borderState",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(borderDarkState) : get(borderLightState);
    },
});

export const greyLighterState = selector<string>({
    key: "greyLighterState",
    get: ({ get }) => brighten(get(greyState), 0.09),
});

export const greyMediumState = selector<string>({
    key: "greyMediumState",
    get: ({ get }) => brighten(get(greyState), -0.16),
});

export const greyDarkerState = selector<string>({
    key: "greyDarkerState",
    get: ({ get }) => brighten(get(greyState), -0.26),
});

export const blackDarkerState = selector<string>({
    key: "blackDarkerState",
    get: ({ get }) => brighten(get(blackMediumState), -0.03),
});

export const blackLighterState = selector<string>({
    key: "blackLighterState",
    get: ({ get }) => {
        const blackMedium = brighten(get(blackMediumState), 0.07);
        const rgb = blackMedium.match(/[0-9]+/g);
        rgb[0] = String(Math.max(0, Number(rgb[0]) - 8));
        rgb[2] = String(Math.min(255, Number(rgb[2]) + 8));
        return `rgb(${rgb.join(",")})`;
    },
});

export const whiteLighterState = selector<string>({
    key: "whiteLighterState",
    get: ({ get }) => brighten(get(whiteMediumState), 0.05),
});

export const whiteDarkerState = selector<string>({
    key: "whiteDarkerState",
    get: ({ get }) => brighten(get(whiteMediumState), -0.03),
});

export const textColorState = selector<string>({
    key: "textColorState",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? (brighten(get(whiteState), -0.1)) : get(blackState);
    },
});

export const backgroundColorState = selector<string>({
    key: "backgroundColor",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(blackMediumState) : get(whiteDarkerState);
    },
});

export const newbackgroundColorState = selector<string>({
    key: "newbackgroundColorState",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(darkDefaultState) : get(whiteState);
    },
});

export const darkerBackgroundColorState = selector<string>({
    key: "darkerBackgroundColor",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(darkDefaultState) : get(whiteDarkerState);
    },
});

export const lighterBackgroundColorState = selector<string>({
    key: "lighterBackgroundColor",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(blackLighterState) : get(whiteLighterState);
    },
});

export const tabLinksColorState = selector<string>({
    key: "tabLinksColorState",
    get: ({ get }) => {
        const theme = get(themeState);
        return theme === "dark" ? get(whiteState) : get(blackState);
    },
});

export const newsAutocompleteState = atom({
    key: "newsAutocompleteState",
    default: undefined,
});

export const filterChipsState = atom({
    key: "filterChipsState",
    default: [],
});

export const popoverContentState = atom<string>({
    key: "popoverContentState",
    default: "rgb(29, 32, 36)",
});

export const userDataState = atom<User>({
    key: "userDataState",
    default: undefined,
});

export const agreementsState = atom<{ code: string }[]>({
    key: "agreementsState",
    default: undefined,
});

export const moduleSettingsState = atom<{ [key: string]: any }>({
    key: "moduleSettingsState",
    default: {},
});

export const stockShortsExpandedState = atom<StockShortsRightColumnModule>({
    key: "stockShortsExpandedState",
    default: "short_shares",
});
export const searchSettingsState = atom<any[]>({
    key: "searchSettingsState",
    default: [],
});

export const tickerTapeState = atom<{ [identifier: string]: Instrument }>({
    key: "tickerTapeState",
    default: {},
});

export const hideTickertapeState = atom<boolean>({
    key: "hideTickertapeState",
    default: true,
});

export const customViewsState = atom<TabTemplate[]>({
    key: "customViewsState",
    default: [],
});

export const customViewsUnsavedState = atom<boolean>({
    key: "customViewsUnsavedState",
    default: false,
});

export const customViewsActiveTabState = atom<number>({
    key: "customViewsActiveTabState",
    default: null,
});

export const customViewsCurrentViewDataState = atom<TabTemplate[]>({
    key: "customViewsCurrentViewDataState",
    default: [],
});

export const customViewsEditModeState = atom<boolean>({
    key: "customViewsEditModeState",
    default: false,
});

export const customViewsSpeedDialState = atom<boolean>({
    key: "customViewsSpeedDialState",
    default: false,
});

export const customViewsCompactTypeState = atom<"horizontal" | "vertical" | null>({
    key: "customViewsCompactTypeState",
    default: "horizontal",
});

export const customViewsPreventCollisionState = atom<boolean>({
    key: "customViewsPreventCollisionState",
    default: false,
});

export const customViewsDraggableItemState = atom<ModuleTemplate>({
    key: "customViewsDraggableItemState",
    default: undefined,
});

export const customViewsComponentsState = atom<ModuleTemplate[]>({
    key: "customViewsComponentsState",
    default: [],
});

export const userState = selector<User>({
    key: "userState",
    get: ({ get }) => ({
        ...get(userDataState),
        settings: {
            theme: get(themeState),
            region: get(regionState),
            universe: get(universeState),
            positive: get(positiveState),
            negative: get(negativeState),
            neutral: get(neutralState),
            grey: get(greyState),
            black: get(blackState),
            white: get(whiteState),
            blackMedium: get(blackMediumState),
            whiteMedium: get(whiteMediumState),
            module_settings: get(moduleSettingsState),
            agreements: get(agreementsState),
            stock_shorts_expanded: get(stockShortsExpandedState),
            search_settings: get(searchSettingsState),
        },
    }),
    set: ({ set }, value: User) => {
        const user = { ...value };
        delete user.settings;
        set(userDataState, value ? user : null);
        if (value) {
            if (value.settings.theme) set(themeState, value.settings.theme);
            set(universeState, value.settings.universe || { id: 7, name: "US & Canada", US_stocks: true });
            set(regionState, isUndefined(value.settings.region) ? 7 : value.settings.region);
            set(userColorSettingsState, {
                positive: value.settings.positive,
                negative: value.settings.negative,
                grey: value.settings.grey,
                blackMedium: value.settings.blackMedium,
            });
            set(positiveState, isUndefined(value.settings.positive) ? "rgb(30,160,105)" : value.settings.positive);
            set(negativeState, isUndefined(value.settings.negative) ? "rgb(226,66,80)" : value.settings.negative);
            set(neutralState, isUndefined(value.settings.neutral) ? "rgb(171,133,109)" : value.settings.neutral);
            set(greyState, isUndefined(value.settings.grey) ? "rgb(161,166,173)" : value.settings.grey);
            set(blackState, isUndefined(value.settings.black) ? "rgb(0,0,0)" : value.settings.black);
            set(whiteState, isUndefined(value.settings.white) ? "rgb(255,255,255)" : value.settings.white);
            set(blackMediumState, isUndefined(value.settings.blackMedium) ? "rgb(22, 24, 27)" : value.settings.blackMedium);
            set(whiteMediumState, isUndefined(value.settings.whiteMedium) ? "rgb(244, 245, 247)" : value.settings.whiteMedium);
            set(moduleSettingsState, isUndefined(value.settings.module_settings) ? {} : value.settings.module_settings);
            set(agreementsState, isUndefined(value.settings.agreements) ? [] : value.settings.agreements);
            set(stockShortsExpandedState, isUndefined(value.settings.stock_shorts_expanded) ? "short_shares" : value.settings.stock_shorts_expanded);
            set(searchSettingsState, isUndefined(value.settings.search_settings) ? [] : value.settings.search_settings);
        }
    },
});

export const clearFiltersState = atom({
    key: "clearFiltersState",
    default: false,
});

export const subscriptionOfferingsState = atom({
    key: "subscriptionOfferingsState",
    default: null,
});

export const appUpgradeLoadingState = atom({
    key: "appUpgradeLoadingState",
    default: false,
});

export const subscriptionState = atom<Subscription>({
    key: "subscriptionState",
    default: null,
});

export const planState = atom<Plan>({
    key: "planState",
    default: null,
});

export const codeAppliedState = atom<string>({
    key: "codeAppliedState",
    default: "",
});

export const promoCodeDisabledState = atom<boolean>({
    key: "promoCodeDisabledState",
    default: true,
});

export const paymentDrawerOpenState = atom({
    key: "paymentDrawerOpenState",
    default: false,
});

export const planPickerDrawerOpenState = atom({
    key: "planPickerDrawerOpenState",
    default: false,
});

export const planPickerSelectedPackageState = atom<any>({
    key: "planPickerSelectedPackageState",
    default: null,
});

export const paymentModalOpenState = atom({
    key: "paymentModalOpenState",
    default: false,
});

export const showAppSubscriberModalState = atom({
    key: "showAppSubscriberModalState",
    default: false,
});

export const notificationsListState = atom<Notification[]>({
    key: "notificationsListState",
    default: [],
});

export const stockTradingItemIdState = atom<number>({
    key: "stockTradingItemIdState",
    default: null,
});

// Stock Report existence — set by RootLayout when the stock page
// mounts (single shared fetch), read by StockLinks (tab insertion +
// "New" badge) and the top-bar badge (in RootLayout itself). Keyed
// to the current stock so a navigation that updates the page also
// updates this; null means "not yet resolved", undefined means
// "resolved and there is no report".
export interface StockReportAvailability {
    exists: boolean;
    headline?: string | null;
    generatedAt?: string | null;
    deepLinkPath?: string | null;
}
export const stockReportAvailabilityState = atom<StockReportAvailability | null>({
    key: "stockReportAvailabilityState",
    default: null,
});

// Trading Idea existence for the current stock — same shape and same
// lifecycle as the Stock Report atom above, set by RootLayout on mount.
//
// The backend probe is GATED: a user without the product group is told
// `exists: false`, indistinguishable from a stock with no idea. So a
// falsy value here means "render nothing", never "render an empty tab"
// — an empty Trading Idea tab would advertise the product AND leak that
// ORTEX holds a published directional view on this particular name.
export interface TradingIdeaAvailability {
    exists: boolean;
    id?: number;
    headline?: string | null;
    direction?: "long" | "short";
    publishedAt?: string | null;
    deepLinkPath?: string | null;
}
export const tradingIdeaAvailabilityState = atom<TradingIdeaAvailability | null>({
    key: "tradingIdeaAvailabilityState",
    default: null,
});

export const showAgreementsModalState = atom({
    key: "showAgreementsModalState",
    default: false,
});

export const alertsListState = atom<any[]>({
    key: "alertsListState",
    default: [],
});

export const tickerWSState = atom<string[]>({
    key: "tickerWSState",
    default: [],
});

export const wsListState = atom<string[]>({
    key: "wsListState",
    default: [],
});

export const filtersDrawerOpenState = atom({
    key: "filtersDrawerOpenState",
    default: false,
});

export const chatMinimizedState = atom<boolean>({
    key: "chatMinimizedState",
    default: false,
});

export const chatModalState = atom<{
    open: boolean;
    initialMessage?: string;
    startingModel?: string;
    chatModel?: string;
}>({
    key: "chatModalState",
    default: {
        open: false,
        initialMessage: undefined,
        startingModel: undefined,
        chatModel: undefined,
    },
});

export const chatModalOpenState = selector({
    key: "chatModalOpenState",
    get: ({ get }) => get(chatModalState).open,
});

// Phone push notifications (ortex/ted/ted#2485). Only ever set inside the ORTEX app.
// Token: the FCM token this device registered with (null until registration succeeds).
export const pushTokenState = atom<string | null>({
    key: "pushTokenState",
    default: null,
});

// OS notification permission as last read from the PushNotifications plugin.
// "unsupported" on desktop web / mobile web / app builds without the plugin.
export type PushPermission = "granted" | "denied" | "prompt" | "unsupported";
export const pushPermissionState = atom<PushPermission>({
    key: "pushPermissionState",
    default: "unsupported",
});

// The "Turn on notifications" pre-prompt. reason "launch" respects the 7-day snooze;
// "alert" is the contextual offer after creating an alert with Push delivery.
export const pushPrePromptState = atom<{ open: boolean; reason: "launch" | "alert" }>({
    key: "pushPrePromptState",
    default: { open: false, reason: "launch" },
});
